<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterDataExchange',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('meterDataExhange');
  const resultText = ref(null);
  const downloadMeterDataExchangeFileURL = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_8622005123045146_validation = computed(() => [
    'ext:xlsx',
    'required',
  ]);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    resultText: resultText.value,
    downloadMeterDataExchangeFileURL: downloadMeterDataExchangeFileURL.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8622005123045146_validation: b_8622005123045146_validation.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_downloadMeterDataExchangeFileURL = () => {
    return $fn.value.urlFor('meters/get_sensor_data_exchange', {
      format: 'xlsx',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'resultText' },
      { name: 'downloadMeterDataExchangeFileURL' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8622005123045146_validation', nodeId: '8622005123045146' },
    ],
    loaders: {
      formId: $$load_formId,
      downloadMeterDataExchangeFileURL: $$load_downloadMeterDataExchangeFileURL,
    },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      resultText,
      downloadMeterDataExchangeFileURL,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8622005123045146_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Meter Data Exchange') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_alr9639sq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1468474219929533_register_t5e1w3b01_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1468474219929533_setData_474fukiv4_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.ngettext(
        'One event has been imported.',
        '${n} events have been imported.',
        resultLength
      ),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1468474219929533_success_fqbpfxk3i_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1468474219929533_setData_2fchne395_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.gettext('No events have been imported.'),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_1468474219929533_success_tdpumgh0c_getWhen = async (
    $event
  ) => {
    return !$event.data.resultLength;
  };
  const act_1468474219929533_request_lzw5swk9f_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'meters/upload_sensor_data_exchange',
      data: { file: file.value },
      contentType: 'multipart/form-data',
    };
  };
  const act_8622005123045146_setData_lwukl6oo7_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'file',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2434514588044777_closeModal_xvr2krihq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_alr9639sq =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_alr9639sq_getActionArgs,
    });
  const inst_evh_7315092382398562_close_eabnr9he7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_alr9639sq,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_eabnr9he7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_gncj60j9n =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_c8uehn9h1 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_gncj60j9n,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_c8uehn9h1],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_7t9mmqaky =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_7ysw8x3g7 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_7t9mmqaky,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7ysw8x3g7],
    event: 'close',
  });
  const act_1468474219929533_register_t5e1w3b01 =
    new actionTypes.RegisterAction({
      actionArgs: act_1468474219929533_register_t5e1w3b01_getActionArgs,
    });
  const inst_evh_1468474219929533_success_ise5vy28b =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_register_t5e1w3b01,
      event: 'success',
      displayName: 'register',
    });
  const act_1468474219929533_setData_474fukiv4 = new actionTypes.SetDataAction({
    actionArgs: act_1468474219929533_setData_474fukiv4_getActionArgs,
  });
  const inst_evh_1468474219929533_success_fqbpfxk3i =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_setData_474fukiv4,
      event: 'success',
      when: inst_evh_1468474219929533_success_fqbpfxk3i_getWhen,
      displayName: 'setData',
    });
  const act_1468474219929533_setData_2fchne395 = new actionTypes.SetDataAction({
    actionArgs: act_1468474219929533_setData_2fchne395_getActionArgs,
  });
  const inst_evh_1468474219929533_success_tdpumgh0c =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_setData_2fchne395,
      event: 'success',
      when: inst_evh_1468474219929533_success_tdpumgh0c_getWhen,
      displayName: 'setData',
    });
  const act_1468474219929533_request_lzw5swk9f = new actionTypes.RequestAction({
    actionArgs: act_1468474219929533_request_lzw5swk9f_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1468474219929533_success_ise5vy28b,
      inst_evh_1468474219929533_success_fqbpfxk3i,
      inst_evh_1468474219929533_success_tdpumgh0c,
    ],
  });
  const inst_evh_1468474219929533_submit_y3zfdl5bk =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_request_lzw5swk9f,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1468474219929533_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_submit_y3zfdl5bk],
    event: 'submit',
  });
  const act_8622005123045146_setData_lwukl6oo7 = new actionTypes.SetDataAction({
    actionArgs: act_8622005123045146_setData_lwukl6oo7_getActionArgs,
  });
  const inst_evh_8622005123045146_updateModelValue_1ptvxtp3x =
    new actionTypes.EventHandlerImpl({
      action: act_8622005123045146_setData_lwukl6oo7,
      event: 'update:modelValue',
      displayName: 'setData',
    });
  const inst_evh_8622005123045146_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8622005123045146_updateModelValue_1ptvxtp3x],
      event: 'update:modelValue',
    });
  const act_2434514588044777_closeModal_xvr2krihq =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_xvr2krihq_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_aqq5gktyq =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_xvr2krihq,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_aqq5gktyq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_eabnr9he7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_c8uehn9h1,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7ysw8x3g7,
    inst_evh_8564662037462133_close,
    inst_evh_1468474219929533_success_ise5vy28b,
    inst_evh_1468474219929533_success_fqbpfxk3i,
    inst_evh_1468474219929533_success_tdpumgh0c,
    inst_evh_1468474219929533_submit_y3zfdl5bk,
    submit: inst_evh_1468474219929533_submit_y3zfdl5bk,
    inst_evh_1468474219929533_submit,
    inst_evh_8622005123045146_updateModelValue_1ptvxtp3x,
    inst_evh_8622005123045146_updateModelValue,
    inst_evh_2434514588044777_clickPreventStop_aqq5gktyq,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_eabnr9he7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_eabnr9he7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_c8uehn9h1 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_c8uehn9h1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7ysw8x3g7 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7ysw8x3g7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_success_ise5vy28b = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_success_ise5vy28b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_success_fqbpfxk3i = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_success_fqbpfxk3i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_success_tdpumgh0c = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_success_tdpumgh0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_submit_y3zfdl5bk = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_submit_y3zfdl5bk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_submit = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8622005123045146_updateModelValue_1ptvxtp3x = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8622005123045146_updateModelValue_1ptvxtp3x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8622005123045146_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_8622005123045146_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_aqq5gktyq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_aqq5gktyq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterDataExchange"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <div>
          <ItpIcon fa="info-circle" class="me-2"> </ItpIcon>
          <ItpText
            wrap
            :text="
              pgettext(
                'MeterDataExchange',
                'The sensor-meter settings are exchanged using an Excel spreadsheet. The current sensor events can be downloaded from here.'
              )
            "
          >
          </ItpText>
          <ItpLink
            :text="pgettext('MeterDataExchange', 'Download events')"
            :href="downloadMeterDataExchangeFileURL"
            button
            variant="light"
            use-auth
            force-download
            class="mx-2"
          >
          </ItpLink>
          <hr />
          <ItpText
            wrap
            :text="
              pgettext(
                'MeterDataExchange',
                'New events can be uploaded to the system using the form below.'
              )
            "
          >
          </ItpText>
        </div>
      </ItpAlert>
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_1468474219929533_submit($event, {})"
        >
          <ItpFormGroup
            name="file"
            :label="pgettext('MeterDataExchange', 'Events file')"
            labelFor="file"
            :description="
              gettext(
                'Only XLSX files are allowed. It must contain at least serial_number and meter_type columns.'
              )
            "
            required
          >
            <ItpFormFile
              :modelValue="file"
              name="file"
              accept=".xlsx"
              required
              :validation="b_8622005123045146_validation"
              @update:modelValue="
                evh_8622005123045146_updateModelValue($event, {})
              "
            >
            </ItpFormFile>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert :show="resultText && !actions.submit.isRunning">
          <p>
            {{ resultText }}
          </p>
        </ItpAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('MeterDataExchange', 'Upload events')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
